import React, { useState, useEffect } from 'react';
import { createSetupIntent, confirmPaymentMethod } from '../services/api';

export default function CardSetup({ onComplete, onSkip }) {
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardReady, setCardReady] = useState(false);

  useEffect(() => {
    // Load Stripe.js dynamically
    const loadStripe = async () => {
      if (window.Stripe) {
        initStripe();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.onload = () => initStripe();
      document.body.appendChild(script);
    };

    const initStripe = async () => {
      try {
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (!publishableKey || publishableKey.includes('your-stripe')) {
          setCardError('Payment setup is being configured. You can add your card later from the dashboard.');
          setLoading(false);
          return;
        }

        const res = await createSetupIntent();
        setClientSecret(res.data.clientSecret);

        const stripeInstance = window.Stripe(publishableKey);
        setStripe(stripeInstance);

        const elementsInstance = stripeInstance.elements({ clientSecret: res.data.clientSecret });
        const cardElement = elementsInstance.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#333',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              '::placeholder': { color: '#aab7c4' },
            },
            invalid: { color: '#ef4444' },
          },
        });

        // Wait for DOM
        setTimeout(() => {
          const mount = document.getElementById('card-element');
          if (mount) {
            cardElement.mount('#card-element');
            cardElement.on('change', (e) => {
              setCardError(e.error?.message || '');
              setCardReady(e.complete);
            });
          }
        }, 100);

        setElements(elementsInstance);
        setLoading(false);
      } catch (err) {
        setCardError('Failed to initialize payment setup. You can add a card later.');
        setLoading(false);
      }
    };

    loadStripe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setProcessing(true);
    setCardError('');

    try {
      const cardElement = elements.getElement('card');
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (error) {
        setCardError(error.message);
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        await confirmPaymentMethod();
        onComplete?.();
      }
    } catch (err) {
      setCardError('Failed to save card. Please try again.');
    }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="glass-card rounded-2xl border border-teal-200 p-8 text-center">
        <div className="animate-pulse">
          <p className="text-teal-600">Setting up payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-2xl border border-teal-200 p-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-800">Payment Method</h3>
          <p className="text-sm text-gray-500">Add a card to download audit reports ($50/report)</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="border border-gray-200 rounded-xl p-4 bg-white">
          <div id="card-element" className="min-h-[24px]" />
        </div>

        {cardError && (
          <p className="text-red-600 text-sm">{cardError}</p>
        )}

        <div className="flex gap-3">
          <button type="submit" disabled={processing || !cardReady}
            className="flex-1 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold py-2.5 rounded-xl shadow-md disabled:opacity-50">
            {processing ? 'Saving...' : 'Save Card'}
          </button>
          {onSkip && (
            <button type="button" onClick={onSkip}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">
              Skip for now
            </button>
          )}
        </div>

        <p className="text-xs text-gray-400 text-center">
          Your card will be charged $50 each time you download an audit report. You can update your card anytime.
        </p>
      </form>
    </div>
  );
}
